import React, { useState, useEffect, useContext } from "react";

import { useQuery } from "@apollo/react-hooks";
import axios from "axios";
import moment from "moment";
import MapGL from "react-map-gl";
import { MapboxLayer } from "@deck.gl/mapbox";
import { Deck } from "@deck.gl/core";
import DeckGL from "@deck.gl/react";
import { IconLayer, LineLayer } from "@deck.gl/layers";

import config from "../../dashboard-config";

/* local components */
import ColorBar from "../ColorBar/ColorBar";
import DataPane from "../DataPane/DataPane";
import DataPaneMobile from "../DataPane/DataPaneMobile";
import GraphPane from "../GraphPane/GraphPane";
import { GET_BEST_TIME_SLICE_WEB_MERCATOR_PNGS } from "../GraphQL/GetBestTimeSliceWebMercatorPng";
import { GET_BEST_TIME_SLICE_WGS_84_PNGS } from "../GraphQL/GetBestTimeSliceWgs84Pngs";

/* contexts for hooks */
import { SpottersContext } from "../../context/SpottersContext";
import { ChartDataContext } from "../../context/ChartDataContext";
import { UIContext } from "../../context/UIContext";
import { SessionContext } from "../../context/SessionContext";

import {
  extractDataForDirectionVisFromResponse,
  makeParticleTrails
} from "./weather-data-utilities";

import "./MapBox.scss";

const getQueryTimestamp = (trailTimestamp, selectedVariableId) => {
  let queryTimestamp = trailTimestamp;
  let hycomMoment = moment(queryTimestamp).utc();
  let hourPart = hycomMoment.hour();

  if (selectedVariableId === 51) {
    // TODO: very brittle, need to remember to hardcode
    hourPart = Math.floor(hourPart / 3) * 3;
  }

  hycomMoment.set({ hour: hourPart, minute: 0, second: 0, ms: 0 });

  return hycomMoment.utc().toISOString();
};

const MIN_SCALE = 0.6;
const MIN_ZOOM = 0.71;
const MAX_ZOOM = 6;
const SCALE_ZOOM_FACTOR = 2;

const boundary = {
  minLongitude: 0.0,
  maxLongitude: 360.0,
  minLatitude: -85.0,
  maxLatitude: 85.0
};

/* viewport zooms */
const smallViewportWidth = window.matchMedia("(max-width: 800px)");
const mediumViewportWidth = window.matchMedia("(max-width: 1000px)");
const tinyViewportHeight = window.matchMedia("(max-height: 550px)");
const smallViewportHeight = window.matchMedia("(max-height: 700px)");
const mediumViewportHeight = window.matchMedia("(max-height: 900px)");

const MapBox = () => {
  const [viewport, setViewport] = useState({
    height: "94vh",
    width: "100vw",
    longitude: -160,
    latitude: 10,
    zoom: 1.7,
    minZoom: 1.15,
    maxZoom: 6
  });

  const _onViewportChange = viewports =>
    setViewport({ ...viewport, ...viewports });

  const [clickedSpotterName, setClickedSpotterName] = useState(null);
  const [highlightedSpotterName, setHighlightedSpotterName] = useState("");
  const [iconLayerData, setIconLayerData] = useState([]);
  const [mapRef, setMapRef] = useState(null);
  const [pointCoordsData, setPointCoordsData] = useState(null);
  const [pointLayerData, setPointLayerData] = useState(null);
  const [viewportState, setViewportState] = useState(null);

  const {
    selectedData,
    selectedOverlayData,
    setSelectedData,
    trailTimestamp
  } = useContext(ChartDataContext);

  const [spotters, setSpotters] = useContext(SpottersContext);
  const { isTouchScreen, setIsDrawerOpen } = useContext(UIContext);
  const profile = JSON.parse(localStorage.getItem("sofar-user") || "{}");

  /* graphql state */
  const [
    bestTimeSliceWebMercatorPng,
    setBestTimeSliceWebMercatorPng
  ] = useState(null);
  const [deck, setDeck] = React.useState();
  const [magnitudeData, setMagnitudeData] = useState(null);
  const [directionData, setDirectionData] = useState(null);

  // WHEN: all data for direction vis is available or path scale is updated
  // DO: construct direction vis
  useEffect(() => {
    if (deck && magnitudeData && directionData) {
      const pathAlpha = 40;

      const lines = makeParticleTrails(
        magnitudeData,
        directionData,
        selectedOverlayData,
        boundary
      );

      const newLineLayer = new LineLayer({
        id: "my-lines-layer",
        data: lines,
        getSourcePosition: l => l.sourcePosition,
        getTargetPosition: l => l.targetPosition,
        getColor: l => l.color,
        opacity: pathAlpha / 255,
        widthMinPixels: 2,
        wrapLongitude: false
      });

      const otherLayers = deck.props.layers.filter(
        l => l.id !== newLineLayer.id
      );

      deck.setProps({ layers: [...otherLayers, newLineLayer] });

      setMagnitudeData(null);
      setDirectionData(null);
    }
  }, [deck, magnitudeData, directionData, selectedOverlayData]);

  useEffect(() => {
    let zoom;
    let height;
    if (tinyViewportHeight.matches) {
      height = "90vh";
      zoom = 1.15;
    } else if (smallViewportHeight.matches) {
      height = "91vh";
      zoom = 1.4;
    } else if (mediumViewportHeight.matches) {
      height = "92.5vh";
      zoom = 1.4;
    } else {
      height = "94vh";
      zoom = 1.7;
    }
    setViewport(v => ({ ...v, height: height, zoom: zoom }));
  }, []);

  useEffect(() => {
    axios
      .get(
        `${config.wavefleetUrl}/oceans-api/latest-data?includeWindData=true`,
        {
          headers: {
            "Content-Type": "application/json",
            token: profile.token
          }
        }
      )
      .then(res => {
        makeData(Object.values(res.data.data));
        setSpotters(Object.values(res.data.data));
        return Object.values(res.data.data);
      })
      .then(res => setSpotters(res))
      .catch(e => {
        console.log(e);
      });
  }, []);

  const makeData = arg => {
    const newIconLayerData = [];

    arg.forEach(spotter => {
      if (
        spotter &&
        spotter.waves &&
        spotter.track &&
        spotter.waves.length > 0 &&
        spotter.track.length > 0
      ) {
        newIconLayerData.push({
          properties: {
            direction: -spotter.waves[0].peakDirection + 180,
            name: spotter.spotterId,
            location: [spotter.track[0].longitude, spotter.track[0].latitude],
            rgb: [255, 255, 255],
            opacity: 1.0,
            size: 2
          },
          geometry: {
            coordinates: [
              spotter.track[0].longitude - 360,
              spotter.track[0].latitude
            ],
            type: "Point"
          },
          type: "Feature"
        });

        newIconLayerData.push({
          properties: {
            direction: -spotter.waves[0].peakDirection + 180,
            name: spotter.spotterId,
            location: [spotter.track[0].longitude, spotter.track[0].latitude],
            rgb: [255, 255, 255],
            opacity: 1.0,
            size: 2
          },
          geometry: {
            coordinates: [
              spotter.track[0].longitude,
              spotter.track[0].latitude
            ],
            type: "Point"
          },
          type: "Feature"
        });

        newIconLayerData.push({
          properties: {
            direction: -spotter.waves[0].peakDirection + 180,
            name: spotter.spotterId,
            location: [spotter.track[0].longitude, spotter.track[0].latitude],
            rgb: [255, 255, 255],
            opacity: 1.0,
            size: 2
          },
          geometry: {
            coordinates: [
              spotter.track[0].longitude + 360,
              spotter.track[0].latitude
            ],
            type: "Point"
          },
          type: "Feature"
        });
      }
    });

    setIconLayerData(...iconLayerData, newIconLayerData);
  };

  const makePointIcon = arg => {
    const [long, lat] = arg;
    const newPointLayerData = [
      {
        properties: {
          name: "Selected Point -360",
          location: [long, lat],
          rgb: [255, 255, 255],
          opacity: 1.0,
          size: 2
        },
        geometry: {
          coordinates: [long - 360, lat],
          type: "Point"
        },
        type: "Feature"
      },
      {
        properties: {
          name: "Selected Point 0",
          location: [long, lat],
          rgb: [255, 255, 255],
          opacity: 1.0,
          size: 2
        },
        geometry: {
          coordinates: [long, lat],
          type: "Point"
        },
        type: "Feature"
      },
      {
        properties: {
          name: "Selected Point +360",
          location: [long, lat],
          rgb: [255, 255, 255],
          opacity: 1.0,
          size: 2
        },
        geometry: {
          coordinates: [long + 360, lat],
          type: "Point"
        },
        type: "Feature"
      }
    ];
    setIsDrawerOpen(true);
    setPointLayerData(newPointLayerData);
  };

  const currentZoom = (viewportState || { zoom: MIN_ZOOM }).zoom;
  const currentSizeScale =
    MIN_SCALE +
    (SCALE_ZOOM_FACTOR * (currentZoom - MIN_ZOOM)) / (MAX_ZOOM - MIN_ZOOM);
  const iconLayer = new IconLayer({
    id: "icon-layer",
    data: iconLayerData,
    pickable: true,
    getAngle: d => d.properties.direction,
    getIcon: d => {
      const isClicked = d.properties.name === clickedSpotterName;
      const isHovered = d.properties.name === highlightedSpotterName;
      if (isClicked)
        return {
          url: "spotter-icon-selected.svg",
          x: 0,
          y: 0,
          width: 70,
          height: 70
        };
      if (!isClicked && isHovered)
        return {
          url: "spotter-icon-hovered.svg",
          x: 0,
          y: 0,
          width: 60,
          height: 60
        };
      if (!isClicked && clickedSpotterName)
        return {
          url: "spotter-icon-unselected.svg",
          x: 0,
          y: 0,
          width: 40,
          height: 40
        };
      return {
        url: "spotter-icon-default.svg",
        x: 0,
        y: 0,
        width: 40,
        height: 40
      };
    },
    updateTriggers: {
      getIcon: [clickedSpotterName, highlightedSpotterName],
      getSize: [highlightedSpotterName]
    },
    sizeScale: currentSizeScale,
    getPosition: d => d.geometry.coordinates,
    getSize: d => (d.properties.name === highlightedSpotterName ? 35 : 30),
    opacity: 1,
    onHover: e => {
      onMouseHover(e);
    },
    onClick: e => {
      onMouseClick(e);
    }
  });

  const pointLayer = new IconLayer({
    id: "point-layer",
    data: pointLayerData,
    pickable: true,
    getIcon: d => ({
      url: "gps-spot.svg",
      x: 0,
      y: 0,
      width: 22,
      height: 22
    }),
    updateTriggers: {
      getIcon: [clickedSpotterName, highlightedSpotterName],
      getSize: [highlightedSpotterName]
    },
    sizeScale: currentSizeScale,
    getPosition: d => d.geometry.coordinates,
    getSize: d => (d.properties.name === highlightedSpotterName ? 35 : 30),
    opacity: 1
  });

  const onMouseHover = hoverInfo => {
    let hoveredName =
      !hoverInfo || !hoverInfo.object || !hoverInfo.picked
        ? ""
        : hoverInfo.object.properties.name;

    setHighlightedSpotterName(hoveredName);
  };

  const onMouseClick = clickedInfo => {
    const clickedName =
      !clickedInfo || !clickedInfo.object || !clickedInfo.picked
        ? ""
        : clickedInfo.object.properties.name;

    setPointLayerData([]);
    setPointCoordsData(null);
    setClickedSpotterName(clickedName);
    setIsDrawerOpen(true);

    const targetSpotter = spotters.filter(
      spotter => spotter.spotterId === clickedName
    );
    setSelectedData({ ...selectedData, selectedSpotter: targetSpotter[0] });
  };

  const _resize = () => {
    let zoom;
    if (smallViewportWidth.matches || smallViewportHeight.matches) {
      zoom = 1;
    } else if (mediumViewportWidth.matches || mediumViewportHeight.matches) {
      zoom = 1.4;
    } else {
      zoom = 1.7;
    }

    _onViewportChange({
      height: "94vh",
      width: "100vw",
      zoom: zoom
    });
  };

  // Whenever the `bestTimeSliceWebMercatorPng` response gets new data,
  // update the Mapbox layers with the new image
  useEffect(() => {
    if (!mapRef || !bestTimeSliceWebMercatorPng) return;

    const source = "data:image/png;base64," + bestTimeSliceWebMercatorPng.data;
    const corners = bestTimeSliceWebMercatorPng.geojsonEnvelope.coordinates[0].slice(
      0,
      4
    );
    // Render wave and current data below the land polygons, and render
    // wind data on top of land but underneath the wind particles
    const belowLayer =
      bestTimeSliceWebMercatorPng.dataSource.id === 2
        ? "land-top"
        : "ne-50m-land";
    const id = "forecast-layer";

    mapRef.addSource(id, {
      type: "image",
      url: source,
      coordinates: corners
    });
    mapRef.addLayer(
      {
        id,
        source: id,
        type: "raster",
        paint: { "raster-opacity": 0.8 }
      },
      belowLayer
    );

    // Clean up and remove the old layers whenever data changes
    return () => {
      mapRef.removeLayer(id);
      mapRef.removeSource(id);
    };
  }, [mapRef, bestTimeSliceWebMercatorPng]);

  /* apollo query */
  useQuery(GET_BEST_TIME_SLICE_WEB_MERCATOR_PNGS, {
    context: { clientName: "wexGeo" },
    fetchPolicy: "cache-and-network",
    variables: {
      input: {
        boundary: [
          {
            longitude: boundary.minLongitude,
            latitude: boundary.minLatitude
          },
          {
            longitude: boundary.maxLongitude,
            latitude: boundary.minLatitude
          },
          {
            longitude: boundary.maxLongitude,
            latitude: boundary.maxLatitude
          },
          {
            longitude: boundary.minLongitude,
            latitude: boundary.maxLatitude
          },
          {
            longitude: boundary.minLongitude,
            latitude: boundary.minLatitude
          }
        ],
        dataSourceId: (selectedOverlayData.selectedDataSource || { id: 0 }).id,
        instant: getQueryTimestamp(
          trailTimestamp,
          selectedOverlayData.selectedVariable.id
        ),
        variableId: (selectedOverlayData.selectedVariable || { id: 0 }).id,
        colorScale: selectedOverlayData.selectedColorScale
      }
    },
    onError: e => console.error(`use query: ${e}`),
    onCompleted: response => {
      if (response.png) setBestTimeSliceWebMercatorPng(response.png);
    }
  });

  useQuery(GET_BEST_TIME_SLICE_WGS_84_PNGS, {
    context: { clientName: "wexGeo" },
    fetchPolicy: "cache-and-network",
    variables: {
      boundary: [
        {
          longitude: boundary.minLongitude,
          latitude: boundary.minLatitude
        },
        {
          longitude: boundary.maxLongitude,
          latitude: boundary.minLatitude
        },
        {
          longitude: boundary.maxLongitude,
          latitude: boundary.maxLatitude
        },
        {
          longitude: boundary.minLongitude,
          latitude: boundary.maxLatitude
        },
        {
          longitude: boundary.minLongitude,
          latitude: boundary.minLatitude
        }
      ],
      dataSourceId: selectedOverlayData.selectedDataSource.id,
      directionVariableId: selectedOverlayData.selectedDirectionalVariableId,
      instant: getQueryTimestamp(
        trailTimestamp,
        selectedOverlayData.selectedVariable.id
      ),
      magnitudeVariableId: selectedOverlayData.selectedMagnitudeVariableId
    },
    onError: e => console.error(`use query: ${e}`),
    onCompleted: response => {
      if (response.magnitudes && response.directions)
        extractDataForDirectionVisFromResponse(response).then(
          ({ magnitude, direction }) => {
            setMagnitudeData(magnitude);
            setDirectionData(direction);
          }
        );
    }
  });

  useEffect(() => {
    window.addEventListener("resize", _resize);

    return () => {
      window.removeEventListener("resize", _resize);
    };
  }, [viewportState]);

  return (
    <React.Fragment>
      <MapGL
        {...viewport}
        attributionControl={false}
        dragRotate={false}
        mapStyle={config.mapboxStyleUrl}
        mapboxApiAccessToken={config.mapboxToken}
        ref={map => {
          if (!mapRef && map) setMapRef(map.getMap());
        }}
        onViewportChange={viewports => {
          setViewportState(viewports);
          _onViewportChange(viewports);
        }}
        onLoad={({ target }) => {
          const deck = new Deck({
            gl: target.painter.context.gl,
            layers: []
          });

          target.addLayer(
            new MapboxLayer({ id: "my-water-layer", deck }),
            "Place 2D Layer HERE"
          );
          target.addLayer(
            new MapboxLayer({ id: "my-wind-layer", deck }),
            "Place 2D Wind HERE"
          );
          target.addLayer(new MapboxLayer({ id: "my-lines-layer", deck }));
          target.addLayer(
            new MapboxLayer({ id: "my-spotter-trail-layer", deck })
          );
          target.addLayer(
            new MapboxLayer({ id: "my-spotter-ghost-layer", deck })
          );

          setDeck(deck);
        }}
      >
        <DeckGL
          viewState={viewport}
          layers={[iconLayer, pointLayer]}
          onClick={e => {
            console.log(e);
            if (!e.picked) {
              let longitudeData = e.lngLat[0];
              if (longitudeData > 0) longitudeData -= 360;

              setPointCoordsData([longitudeData, e.lngLat[1]]);
              // setSkipQuery(false);
              setClickedSpotterName(null);
              setSelectedData({ ...selectedData, selectedSpotter: null });
              makePointIcon(e.lngLat);
            }
          }}
        />
      </MapGL>
      <ColorBar />
      {selectedData.selectedSpotter || pointCoordsData ? (
        <React.Fragment>
          {isTouchScreen ? (
            <DataPaneMobile
              pointData={pointCoordsData}
              selectedSpotter={selectedData.selectedSpotter}
              spotterData={selectedData.selectedSpotter}
            />
          ) : (
            <React.Fragment>
              <DataPane
                pointData={pointCoordsData}
                spotterData={selectedData.selectedSpotter}
              />
              <GraphPane
                deck={deck}
                // isToggled={isToggled}
                pointData={pointCoordsData}
                sizeScale={currentSizeScale}
                selectedSpotter={selectedData.selectedSpotter}
              />
            </React.Fragment>
          )}
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default MapBox;
